import React from "react";

interface InputFieldProps {
    type: string;
    value: string | number;
    onChange: (value: any) => void; // Accept string or number
    label?: string;
    placeholder?: string;
    required?: boolean;
    id?: string;
    step?: string;
    min?: string;
    multiline?: boolean;
    rows?: number;
    fullWidth?: boolean;
    tooltip?: string; // New prop for tooltip
}

const InputField: React.FC<InputFieldProps> = ({
                                                   type,
                                                   value,
                                                   onChange,
                                                   label,
                                                   placeholder,
                                                   required = false,
                                                   id,
                                                   multiline = false,
                                                   rows,
                                                   fullWidth = true,
                                                   tooltip,
                                               }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputValue = e.target.value;

        // Allow valid decimal values in the input
        if (type === "number" && !/^\d*\.?\d*$/.test(inputValue)) {
            return; // Ignore invalid input for numbers
        }

        onChange(inputValue);
    };

    const handleBlur = () => {
        if (type === "number" && typeof value === "string") {
            const numericValue = parseFloat(value);
            if (!isNaN(numericValue)) {
                onChange(numericValue); // Convert string to number on blur
            } else {
                onChange(0); // Default to 0 if the input is invalid
            }
        }
    };

    return (
        <div className={`input mb-4 ${fullWidth ? "w-full" : "w-auto"}`}>
            {label && (
                <div className="flex items-center mb-2">
                    <label htmlFor={id}>
                        {label}{" "}
                        {required ? (
                            <span className="text-red-600">*</span> // Show red asterisk for required fields
                        ) : (
                            "(Optional)" // Add "(Optional)" for non-required fields
                        )}
                    </label>
                    {tooltip && (
                        <div className="ml-2 relative group">
                            <span className="tooltip-icon">i</span>
                            <span className="tooltip-text">{tooltip}</span>
                        </div>
                    )}
                </div>
            )}

            {multiline ? (
                <textarea
                    id={id}
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    required={required}
                    rows={rows}
                    onBlur={handleBlur}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    required={required}
                    id={id}
                    onBlur={handleBlur}
                />
            )}
        </div>
    );
};

export default InputField;